import React, { FC, useRef } from 'react';
import styled from 'styled-components';

import { focusShadow } from '#/components/styles';
import { noop, remify } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

import { FormButtonContainer, FormMarkdown } from '../../Form.styled';
import { getErrorMessagesIds } from '../../Form.utils';
import { Container } from '../FormInput.styled';
import { CommonInputProps } from '../FormInput.types';
import { ErrorMessage } from './ErrorMessage';

const CheckboxFieldContainer = styled(Container)`
  margin: ${remify(8)} 0 ${remify(24)} 0;

  /* Prevent double margin between consecutive checkboxes */
  & + & {
    margin-top: 0;
  }

  /* Prevent double margin when buttons follow a checkbox */
  & + ${FormButtonContainer} {
    margin-top: 0;
  }
`;

interface InputCheckboxProps extends CommonInputProps {
  inputValue: boolean;
}

interface CheckBoxProps {
  $touched?: boolean;
  $hasError?: boolean;
  $checked?: boolean;
}

const CheckboxContainer = styled.div<CheckBoxProps>`
  display: flex;
  align-items: center;
  gap: ${remify(8)};
  cursor: pointer;

  /* State styles */
  ${({ $checked }) =>
    !$checked &&
    `
    svg path {
      fill: var(--pageParagraphForeground);
    }
    
    &:hover svg path {
      fill: var(--pageParagraphForegroundSubtle);
    }
  `}

  ${({ $checked }) =>
    $checked &&
    `
    .checkbox-checked-background {
      fill: var(--selectionControlsBackground);
    }
    .checkbox-checked-checkmark {
      fill: var(--selectionControlsForeground);
    }
    &:hover .checkbox-checked-background {
      fill: var(--selectionControlsBackgroundHover);
    }
  `}

  /* Error state styling */
  ${({ $hasError, $touched }) =>
    $hasError &&
    $touched &&
    `
    svg path {
      fill: var(--inputValidation);
    }

    &:hover svg path {
      fill: var(--inputValidationHover);
    }
  `}
`;

const StyledLabel = styled.label`
  flex: 1;
  cursor: pointer;
  user-select: none;

  & * {
    font: var(--fontStyleFootnote);
  }
`;

const CheckBoxWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  outline: none;
  border-radius: ${remify(4)};

  input:focus-visible + svg {
    border-radius: ${remify(4)};
    ${focusShadow}
  }
`;

export const InputCheckbox: FC<InputCheckboxProps> = ({
  dataKey,
  errorMessages,
  inputValue,
  required,
  label = '',
  touched,
  updateErrorMessages = noop,
  updateInputValue = noop,
  handleBlur = noop,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasError = !!errorMessages?.length;

  const validate = () => {
    updateErrorMessages(dataKey, inputRef.current?.validationMessage);
  };

  const handleChange = (value: boolean) => {
    updateInputValue(dataKey, {
      touched: true,
      value,
    });
    validate();
  };

  const handleOnKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault(); // Prevent space from scrolling the page
      inputRef.current?.click();
    }
  };

  return (
    <CheckboxFieldContainer>
      <CheckboxContainer
        $checked={inputValue}
        $hasError={hasError}
        $touched={touched}
      >
        <CheckBoxWrapper
          data-testid="checkbox-wrapper"
          onClick={() => inputRef.current?.click()}
          onKeyDown={handleOnKeyDown}
        >
          <input
            aria-describedby={
              hasError
                ? getErrorMessagesIds({ dataKey, errorMessages })
                : undefined
            }
            aria-labelledby={`${dataKey}-label`}
            checked={inputValue}
            className="visually-hidden"
            data-testid="input-checkbox"
            id={dataKey}
            name={dataKey}
            onBlur={() => {
              handleBlur();
              validate();
            }}
            onChange={(e) => handleChange(e.target.checked)}
            ref={inputRef}
            required={required}
            type="checkbox"
          />
          {inputValue ? <CheckboxChecked /> : <CheckboxUnchecked />}
        </CheckBoxWrapper>
        <StyledLabel htmlFor={dataKey} id={`${dataKey}-label`}>
          <FormMarkdown>{tb(label)}</FormMarkdown>
        </StyledLabel>
      </CheckboxContainer>
      {errorMessages?.map((errorMessage, index) => (
        <ErrorMessage
          aria-live="assertive"
          id={`${dataKey}-error-message-${index}`}
          key={`${dataKey}-error-message-${index}`}
        >
          {errorMessage}
        </ErrorMessage>
      ))}
    </CheckboxFieldContainer>
  );
};

const CheckboxChecked = () => (
  <svg
    fill="none"
    height="24"
    viewBox="0 0 24 24"
    width="24"
    xmlns="https://www.w3.org/2000/svg"
  >
    <path
      className="checkbox-checked-background"
      clipRule="evenodd"
      d="M17.4 3C19.3882 3 21 4.61177 21 6.6V17.4C21 19.3882 19.3882 21 17.4 21H6.6C4.61177 21 3 19.3882 3 17.4V6.6C3 4.61177 4.61177 3 6.6 3H17.4Z"
      fillRule="evenodd"
    />
    <path
      className="checkbox-checked-checkmark"
      clipRule="evenodd"
      d="M16.2475 8.29289C16.638 7.90237 17.2712 7.90237 17.6617 8.29289C18.0222 8.65338 18.0499 9.22061 17.7449 9.6129L17.6617 9.70711L11.0189 16.35L6.36239 12.4957C5.93695 12.1435 5.87753 11.5132 6.22969 11.0877C6.55475 10.695 7.11688 10.6142 7.53606 10.881L7.63766 10.955L10.892 13.648L16.2475 8.29289Z"
      fillRule="evenodd"
    />
  </svg>
);

const CheckboxUnchecked = () => (
  <svg
    fill="none"
    height="24"
    viewBox="0 0 24 24"
    width="24"
    xmlns="https://www.w3.org/2000/svg"
  >
    <path
      className="checkbox-unchecked-background"
      clipRule="evenodd"
      d="M17.4 3C19.3882 3 21 4.61177 21 6.6V17.4C21 19.3882 19.3882 21 17.4 21H6.6C4.61177 21 3 19.3882 3 17.4V6.6C3 4.61177 4.61177 3 6.6 3H17.4ZM17.4 5H6.6C5.76544 5 5.08011 5.63896 5.00654 6.45437L5 6.6V17.4C5 18.2346 5.63896 18.9199 6.45437 18.9935L6.6 19H17.4C18.2346 19 18.9199 18.361 18.9935 17.5456L19 17.4V6.6C19 5.76544 18.361 5.08011 17.5456 5.00654L17.4 5Z"
      fillRule="evenodd"
    />
  </svg>
);
